import {
  Button,
  ChevronRight,
  FolderOpen,
  HelpCircle,
  Home,
  Input,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { Modal } from "../../components/molecules/Modal/Modal";
import { faqApi } from "../../services/faq.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  createFaqRequest,
  deleteFaqRequest,
  fetchFaqsRequest,
  selectFaqs,
  selectFaqsDeleting,
  selectFaqsLoadedLang,
  selectFaqsLoading,
  selectFaqsSaving,
  updateFaqRequest,
} from "../../store/reducers/faqSlice";
import { FaqDto, FaqLabelVM } from "../../types/faq.definition";
import { textDirection } from "../../utils/text-direction-utils";

type LangKey = "en" | "fr" | "ar";
const LANGS: { key: LangKey; label: string }[] = [
  { key: "en", label: "English" },
  { key: "fr", label: "Français" },
  { key: "ar", label: "العربية" },
];

// ─── HTML sanitizer (client-side, before sending to BFF) ──────────────────────

const ALLOWED_TAGS = ["b", "ul", "ol", "li", "a", "p", "br"];

function sanitizeNode(node: Node): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toLowerCase();
      if (!ALLOWED_TAGS.includes(tag)) {
        // Unwrap: replace element with its children
        while (el.firstChild) node.insertBefore(el.firstChild, el);
        node.removeChild(el);
      } else {
        // Strip all attributes except href on <a>
        Array.from(el.attributes).forEach(attr => {
          if (tag === "a" && attr.name === "href") return;
          el.removeAttribute(attr.name);
        });
        sanitizeNode(el);
      }
    }
  }
}

function sanitizeHtml(html: string): string {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  sanitizeNode(doc.body);
  return doc.body.innerHTML;
}

// ─── Rich Text Editor ─────────────────────────────────────────────────────────

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [showLinkPanel, setShowLinkPanel] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [isBoldActive, setIsBoldActive] = useState(false);

  // Set initial content on mount only (key={activeLang} remounts on tab switch)
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track bold state whenever the selection moves inside the editor
  useEffect(() => {
    const onSelectionChange = () => {
      const editor = editorRef.current;
      const sel = window.getSelection();
      if (editor && sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (editor.contains(range.commonAncestorContainer)) {
          setIsBoldActive(document.queryCommandState("bold"));
          return;
        }
      }
      setIsBoldActive(false);
    };
    document.addEventListener("selectionchange", onSelectionChange);
    return () =>
      document.removeEventListener("selectionchange", onSelectionChange);
  }, []);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(sanitizeHtml(editorRef.current.innerHTML));
    }
  };

  // ── Bold ──────────────────────────────────────────────────────────────────
  const handleBold = (e: React.MouseEvent) => {
    e.preventDefault();
    editorRef.current?.focus();

    document.execCommand("bold", false);
    handleInput();
  };

  // ── Lists: manual DOM insertion (more reliable than execCommand) ───────────
  const insertList = (type: "ul" | "ol") => (e: React.MouseEvent) => {
    e.preventDefault();
    const editor = editorRef.current;
    if (!editor) return;
    editor.focus();

    const sel = window.getSelection();
    if (!sel) return;

    // If nothing is focused inside the editor, place cursor at end
    if (sel.rangeCount === 0) {
      const r = document.createRange();
      r.selectNodeContents(editor);
      r.collapse(false);
      sel.addRange(r);
    }

    const range = sel.getRangeAt(0);
    const list = document.createElement(type);
    const li = document.createElement("li");

    // Wrap any selected content into the first <li>
    if (!range.collapsed) {
      li.appendChild(range.extractContents());
    }

    list.appendChild(li);
    range.deleteContents();
    range.insertNode(list);

    // Place cursor inside the <li>
    const newRange = document.createRange();
    newRange.setStart(li, li.childNodes.length);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);

    onChange(sanitizeHtml(editor.innerHTML));
  };

  // ── Link ──────────────────────────────────────────────────────────────────
  const openLinkPanel = (e: React.MouseEvent) => {
    e.preventDefault();
    // Save current cursor/selection so we can insert at that position later
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
      // Pre-fill text field with any selected text
      setLinkText(sel.toString());
    } else {
      savedRangeRef.current = null;
      setLinkText("");
    }
    setLinkUrl("");
    setShowLinkPanel(true);
  };

  const closeLinkPanel = () => {
    setShowLinkPanel(false);
    setLinkText("");
    setLinkUrl("");
    savedRangeRef.current = null;
  };

  // ── Click on existing link → open panel pre-filled for editing ────────────
  const handleEditorClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (e.target as Element).closest("a");
    if (!anchor) return;
    e.preventDefault();
    // Select the whole anchor so handleInsertLink replaces it in-place
    const range = document.createRange();
    range.selectNode(anchor);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    savedRangeRef.current = range.cloneRange();
    setLinkText(anchor.textContent ?? "");
    setLinkUrl(anchor.getAttribute("href") ?? "");
    setShowLinkPanel(true);
  };

  const handleInsertLink = () => {
    const editor = editorRef.current;
    if (!editor || !linkUrl) return;

    editor.focus();

    // Restore the selection that was active when the panel was opened
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }

    const range =
      sel && sel.rangeCount > 0
        ? sel.getRangeAt(0)
        : (() => {
            const r = document.createRange();
            r.selectNodeContents(editor);
            r.collapse(false);
            return r;
          })();

    range.deleteContents();

    const a = document.createElement("a");
    a.href = linkUrl;
    a.textContent = linkText.trim() || linkUrl;
    range.insertNode(a);

    // Move cursor after the inserted link
    const newRange = document.createRange();
    newRange.setStartAfter(a);
    newRange.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(newRange);

    onChange(sanitizeHtml(editor.innerHTML));
    closeLinkPanel();
  };

  const toolbarBtn =
    "px-2 py-1 text-sm rounded hover:bg-neutral-200 active:bg-neutral-300 transition-colors select-none";
  const toolbarBtnActive =
    "bg-ews-primary/10 text-ews-primary ring-1 ring-ews-primary/20";

  return (
    <div className="border border-neutral-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-ews-primary/30 focus-within:border-ews-primary">
      {/* Toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-neutral-50 border-b border-neutral-200">
        <button
          type="button"
          onMouseDown={handleBold}
          className={`${toolbarBtn} font-bold ${isBoldActive ? toolbarBtnActive : ""}`}
          title="Bold"
        >
          B
        </button>
        <button
          type="button"
          onMouseDown={insertList("ul")}
          className={toolbarBtn}
          title="Bullet list"
        >
          UL
        </button>
        <button
          type="button"
          onMouseDown={insertList("ol")}
          className={toolbarBtn}
          title="Numbered list"
        >
          OL
        </button>
        <button
          type="button"
          onMouseDown={openLinkPanel}
          className={`${toolbarBtn} ${showLinkPanel ? toolbarBtnActive : ""}`}
          title="Insert link"
        >
          Link
        </button>
      </div>

      {/* Link panel — text + url */}
      {showLinkPanel && (
        <div className="flex flex-col gap-1.5 px-2 py-2 bg-neutral-50 border-b border-neutral-200">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={linkText}
              onChange={e => setLinkText(e.target.value)}
              placeholder="Display text"
              className="w-32 px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-ews-primary"
            />
            <input
              type="url"
              value={linkUrl}
              onChange={e => setLinkUrl(e.target.value)}
              placeholder="https://..."
              className="flex-1 min-w-[160px] px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-ews-primary"
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleInsertLink();
                }
                if (e.key === "Escape") closeLinkPanel();
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={handleInsertLink}
              className="px-2 py-1 text-sm bg-ews-primary text-white rounded hover:bg-ews-primary/90"
            >
              OK
            </button>
            <button
              type="button"
              onClick={closeLinkPanel}
              className="px-2 py-1 text-sm text-neutral-500 hover:text-neutral-700"
            >
              ✕
            </button>
          </div>
          <p className="text-xs text-neutral-400 italic">
            {t("admin.faq.form.linkHint")}
          </p>
        </div>
      )}

      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onClick={handleEditorClick}
        className="min-h-[120px] px-3 py-2 text-sm focus:outline-none empty:before:content-[attr(data-placeholder)] empty:before:text-neutral-400 empty:before:pointer-events-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_a]:text-blue-600 [&_a]:underline [&_a]:cursor-pointer"
        data-placeholder={placeholder}
        suppressContentEditableWarning
      />
    </div>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyLabel(): FaqLabelVM {
  return {
    en: { title: "", description: "" },
    fr: { title: "", description: "" },
    ar: { title: "", description: "" },
  };
}

function faqToLabel(faq: FaqDto, currentLang: LangKey): FaqLabelVM {
  return {
    ...emptyLabel(),
    [currentLang]: {
      title: faq.title ?? "",
      description: faq.description ?? "",
    },
  };
}

// A breadcrumb node: the FAQ we drilled into, plus its displayed title
interface BreadcrumbNode {
  faq: FaqDto;
}

/** Recursively flatten a tree of FAQs into a flat array (for the parent dropdown). */
function flattenTree(list: FaqDto[]): FaqDto[] {
  return list.flatMap(f => [f, ...flattenTree(f.children ?? [])]);
}

// ─── Component ────────────────────────────────────────────────────────────────

export const FaqManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Redux state — `faqs` holds the full tree (root nodes with nested children)
  const faqs = useAppSelector(selectFaqs);
  const loadedLang = useAppSelector(selectFaqsLoadedLang);
  const isLoading = useAppSelector(selectFaqsLoading);
  const isSaving = useAppSelector(selectFaqsSaving);
  const isDeleting = useAppSelector(selectFaqsDeleting);

  const [lang, setLang] = useState<LangKey>("fr");

  // Breadcrumb stack: each node is a parent we drilled into
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbNode[]>([]);

  // The current parent context (null = root level)
  const currentParent =
    breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].faq : null;

  // Derive the visible FAQ list by traversing the tree along the breadcrumb path.
  // No extra API call needed — the full tree is already in Redux.
  const currentFaqs = useMemo(() => {
    if (breadcrumbs.length === 0) return faqs;
    let level: FaqDto[] = faqs;
    for (const node of breadcrumbs) {
      const found = level.find(f => f.id === node.faq.id);
      if (!found) return [];
      level = found.children ?? [];
    }
    return level;
  }, [faqs, breadcrumbs]);

  // All FAQs as a flat list (for the parent dropdown in the form)
  const allFaqs = useMemo(() => flattenTree(faqs), [faqs]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqDto | null>(null);

  // Form state
  const [activeLang, setActiveLang] = useState<LangKey>("fr");
  const [orderIndex, setOrderIndex] = useState(0);
  const [parentId, setParentId] = useState<number | null>(null);
  const [label, setLabel] = useState<FaqLabelVM>(emptyLabel());

  const [isLoadingTranslations, setIsLoadingTranslations] = useState(false);

  // Delete confirmation
  const [deletingFaqId, setDeletingFaqId] = useState<number | null>(null);

  const loadFaqs = (currentLang: LangKey = lang) => {
    dispatch(fetchFaqsRequest({ lang: currentLang }));
  };

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Load only when store is empty or language changed — breadcrumb navigation is local
  useEffect(() => {
    if (faqs.length === 0 || loadedLang !== lang) {
      loadFaqs(lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const handleRefresh = () => loadFaqs();

  // ─── Drill down into children ─────────────────────────────────────────────
  const drillInto = (faq: FaqDto) => {
    if (currentParent?.id === faq.id) return;
    setBreadcrumbs(prev => [...prev, { faq }]);
  };

  // Navigate to a breadcrumb node (root = index -1)
  const navigateTo = (index: number) => {
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  };

  const navigateToRoot = () => {
    setBreadcrumbs([]);
  };

  // ─── Modal helpers ────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setEditingFaq(null);
    setOrderIndex(0);
    setParentId(currentParent?.id ?? null);
    setLabel(emptyLabel());
    setActiveLang("fr");
    setIsModalOpen(true);
  };

  const openEditModal = (faq: FaqDto) => {
    setEditingFaq(faq);
    setOrderIndex(faq.orderIndex);
    setParentId(faq.parentId);
    // Populate only the currently displayed language immediately so the modal opens without delay
    setLabel(faqToLabel(faq, lang));
    setActiveLang("fr");
    setIsModalOpen(true);

    // Fetch all language translations in parallel and replace the label
    setIsLoadingTranslations(true);
    faqApi
      .getFaqAllTranslations(faq.id)
      .then(allTranslations => setLabel(allTranslations))
      .catch(() => {
        /* keep the current-language fallback already set above */
      })
      .finally(() => setIsLoadingTranslations(false));
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFaq(null);
  };

  const handleSave = () => {
    const payload = { parentId, orderIndex, label };

    if (editingFaq) {
      dispatch(
        updateFaqRequest({
          id: editingFaq.id,
          data: payload,
          onSuccess: () => {
            closeModal();
            loadFaqs();
          },
        })
      );
    } else {
      dispatch(
        createFaqRequest({
          data: payload,
          onSuccess: () => {
            closeModal();
            loadFaqs();
          },
        })
      );
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingFaqId === null) return;
    const idToDelete = deletingFaqId;
    setDeletingFaqId(null);
    dispatch(
      deleteFaqRequest({
        id: idToDelete,
        onSuccess: () => loadFaqs(),
        onError: (code: string) => {
          if (code === "FAQ_HAS_CHILDREN") {
            toast.error(t("admin.faq.deleteHasChildrenError"));
          } else {
            toast.error(t("admin.faq.deleteError"));
          }
        },
      })
    );
  };

  const currentLangData = label[activeLang] ?? { title: "", description: "" };

  return (
    <div className="page-container">
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-neutral-900">
            {t("admin.faq.title")}
          </h2>
          <p className="text-neutral-600">{t("admin.faq.subtitle")}</p>
        </div>
        <div className="flex items-center space-x-2">
          {/* Language selector */}
          <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
            {LANGS.map(({ key, label: lbl }) => (
              <button
                key={key}
                onClick={() => setLang(key)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  lang === key
                    ? "bg-ews-primary text-white"
                    : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center space-x-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`}
            />
            <span>{t("common.refresh")}</span>
          </Button>
          <Button
            onClick={openCreateModal}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t("admin.faq.addNew")}</span>
          </Button>
        </div>
      </div>

      {/* ─── Breadcrumb ──────────────────────────────────────────────────── */}
      <div className="flex items-center space-x-1 text-sm text-neutral-600 page-section pb-0">
        <button
          onClick={navigateToRoot}
          className={`flex items-center space-x-1 hover:text-ews-primary transition-colors ${
            breadcrumbs.length === 0
              ? "font-semibold text-neutral-900 pointer-events-none"
              : ""
          }`}
        >
          <Home className="w-3.5 h-3.5" />
          <span>{t("admin.faq.breadcrumb.root")}</span>
        </button>
        {breadcrumbs.map((node, idx) => (
          <React.Fragment key={node.faq.id}>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-400" />
            <button
              onClick={() => navigateTo(idx)}
              className={`hover:text-ews-primary transition-colors truncate max-w-[200px] ${
                idx === breadcrumbs.length - 1
                  ? "font-semibold text-neutral-900 pointer-events-none"
                  : ""
              }`}
              title={node.faq.title}
            >
              {node.faq.title || `#${node.faq.id}`}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* ─── Table ───────────────────────────────────────────────────────── */}
      <div className="page-section">
        {isLoading ? (
          <div className="py-12 text-center">
            <RefreshCw className="mx-auto w-8 h-8 animate-spin text-neutral-400" />
            <p className="mt-2 text-sm text-neutral-500">
              {t("common.loading")}
            </p>
          </div>
        ) : currentFaqs.length === 0 ? (
          <div className="py-12 text-center">
            <HelpCircle className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {currentParent
                ? t("admin.faq.noChildren")
                : t("admin.faq.noFaqs")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {currentParent
                ? t("admin.faq.noChildrenDescription")
                : t("admin.faq.noFaqsDescription")}
            </p>
            <div className="mt-6">
              <Button onClick={openCreateModal}>
                <Plus className="mr-2 w-4 h-4" />
                {t("admin.faq.addNew")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600 w-16">
                    {t("admin.faq.table.id")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600">
                    {t("admin.faq.table.title")}
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-600 w-20">
                    {t("admin.faq.table.order")}
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-600 w-36">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {currentFaqs.map(faq => (
                  <tr
                    key={faq.id}
                    className="hover:bg-neutral-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-neutral-500 font-mono text-xs">
                      #{faq.id}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium text-neutral-900">
                        {faq.title || (
                          <span className="italic text-neutral-400">
                            {t("admin.faq.noTitle")}
                          </span>
                        )}
                      </span>
                      {faq.description && (
                        <p className="mt-0.5 text-xs text-neutral-500 truncate max-w-md">
                          {faq.description.replace(/<[^>]*>/g, "")}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 text-center">
                      {faq.orderIndex}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center space-x-1">
                        {/* View children button */}
                        <button
                          onClick={() => drillInto(faq)}
                          disabled={isLoading}
                          className="flex items-center space-x-1 px-2 py-1.5 rounded text-xs font-medium text-neutral-600 hover:bg-ews-primary/10 hover:text-ews-primary transition-colors disabled:opacity-40 disabled:pointer-events-none"
                          title={t("admin.faq.viewChildren")}
                        >
                          <FolderOpen className="w-3.5 h-3.5" />
                          <span>{t("admin.faq.children")}</span>
                        </button>

                        {/* Edit button */}
                        <button
                          onClick={() => openEditModal(faq)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-neutral-100 hover:text-ews-primary transition-colors"
                          title={t("common.edit")}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>

                        {/* Delete button */}
                        <button
                          onClick={() => setDeletingFaqId(faq.id)}
                          className="p-1.5 rounded text-neutral-600 hover:bg-danger-50 hover:text-danger-600 transition-colors"
                          title={t("common.delete")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Create / Edit Modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        size="lg"
        title={editingFaq ? t("admin.faq.editFaq") : t("admin.faq.addNew")}
      >
        <div className="p-6 space-y-5">
          {/* Order + Parent */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.faq.form.orderIndex")}
              </label>
              <Input
                type="number"
                size="sm"
                value={String(orderIndex)}
                onChange={e => setOrderIndex(parseInt(e.target.value) || 0)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                {t("admin.faq.form.parent")}
              </label>
              <select
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ews-primary/30 focus:border-ews-primary"
                value={parentId ?? ""}
                onChange={e =>
                  setParentId(e.target.value ? parseInt(e.target.value) : null)
                }
              >
                <option value="">{t("admin.faq.form.noParent")}</option>
                {allFaqs
                  .filter(f => !editingFaq || f.id !== editingFaq.id)
                  .map(f => (
                    <option key={f.id} value={f.id}>
                      #{f.id} —{" "}
                      {f.title?.slice(0, 50) || t("admin.faq.noTitle")}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Language tabs */}
          <div>
            <div className="flex items-center border-b border-neutral-200 mb-4">
              {LANGS.map(({ key, label: lbl }) => (
                <button
                  key={key}
                  onClick={() => setActiveLang(key)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    activeLang === key
                      ? "border-ews-primary text-ews-primary"
                      : "border-transparent text-neutral-600 hover:text-neutral-900"
                  }`}
                >
                  {lbl}
                </button>
              ))}
              {isLoadingTranslations && (
                <span className="ml-auto flex items-center gap-1 text-xs text-neutral-400 pb-1">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  {t("common.loading")}
                </span>
              )}
            </div>

            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  {t("admin.faq.form.title")}
                </label>
                <Input
                  size="sm"
                  dir={textDirection(activeLang)}
                  value={currentLangData.title ?? ""}
                  onChange={e =>
                    setLabel(prev => ({
                      ...prev,
                      [activeLang]: {
                        ...prev[activeLang],
                        title: e.target.value,
                      },
                    }))
                  }
                  placeholder={t("admin.faq.form.titlePlaceholder")}
                />
              </div>

              {/* Description — only for child FAQs (parentId !== null) */}
              {parentId !== null && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {t("admin.faq.form.description")}
                  </label>
                  <RichTextEditor
                    key={`${activeLang}-${isLoadingTranslations ? "loading" : "loaded"}`}
                    value={currentLangData.description ?? ""}
                    onChange={html =>
                      setLabel(prev => ({
                        ...prev,
                        [activeLang]: {
                          ...prev[activeLang],
                          description: html,
                        },
                      }))
                    }
                    placeholder={t("admin.faq.form.descriptionPlaceholder")}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-2 border-t border-neutral-200">
            <Button variant="outline" onClick={closeModal} disabled={isSaving}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave} loading={isSaving} disabled={isSaving}>
              {t("common.save")}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ─── Delete confirmation ──────────────────────────────────────────── */}
      <ConfirmationModal
        isOpen={deletingFaqId !== null}
        onClose={() => setDeletingFaqId(null)}
        onConfirm={handleDeleteConfirm}
        title={t("admin.faq.deleteConfirm.title")}
        message={t("admin.faq.deleteConfirm.message")}
        variant="danger"
        isLoading={isDeleting}
        confirmLabel={t("common.delete")}
      />
    </div>
  );
};
